import React, { useCallback, useEffect, useRef, useState } from 'react';
import { JuniorLayout } from '../../components/layout/JuniorLayout';
import { ActivityShell } from '../../components/activity/ActivityShell';
import { EcoLayoutTuningProvider } from '../../contexts/EcoLayoutTuningContext';
import { EcoRitmoBuilder } from '../../components/ecoRitmico/EcoRitmoBuilder';
import { EcoRitmoStage, type EcoPhase } from '../../components/ecoRitmico/EcoRitmoStage';
import { RequireJuniorSession } from '../../components/auth/RequireJuniorSession';
import { useOnsetDetection } from '../../hooks/useOnsetDetection';
import {
  ECO_SOUND_PATHS,
  buildExerciseFromConfig,
  createDefaultSessionConfig,
  type EcoRitmoExercise,
} from '../../lib/rhythm/ecoRitmoConfig';
import { loadEcoMicSettings, type EcoMicSettings } from '../../lib/rhythm/ecoMicConfig';
import {
  PresentationModeProvider,
  usePresentationMode,
} from '../../contexts/PresentationModeContext';
import {
  RhythmTapEngine,
  attackShouldSound,
} from '../../lib/rhythm/rhythmTapEngine';
import { playPercusionSound, preloadPercusionSounds } from '../../lib/usePercusionSounds';

const COUNTDOWN_BEATS = 3;
const DEMO_TAIL_MS = 600;

type PagePhase = 'lobby' | EcoPhase;

function EcoRitmicoPageInner() {
  const { isPresentation, setPresentation } = usePresentationMode();
  const soundPathRef = useRef(ECO_SOUND_PATHS.clap);
  const [exercise, setExercise] = useState<EcoRitmoExercise>(() =>
    buildExerciseFromConfig(createDefaultSessionConfig())
  );
  const [micSettings, setMicSettings] = useState<EcoMicSettings>(() => loadEcoMicSettings());
  const [pagePhase, setPagePhase] = useState<PagePhase>('lobby');
  const [engine, setEngine] = useState(() =>
    new RhythmTapEngine(buildExerciseFromConfig(createDefaultSessionConfig()))
  );
  const [activeCellIndex, setActiveCellIndex] = useState(-1);
  const [countdown, setCountdown] = useState(COUNTDOWN_BEATS);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [faultCount, setFaultCount] = useState(0);
  const [tapFlash, setTapFlash] = useState<'hit' | 'fault' | null>(null);

  const engineRef = useRef(engine);
  engineRef.current = engine;

  const phaseStartRef = useRef(0);
  const playedAttacksRef = useRef<Set<number>>(new Set());
  const flashTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const micUsesMic = micSettings.inputMode !== 'tap';
  const {
    micActive,
    volume: micVolume,
    micError,
    startDetection,
    stopDetection,
    setOnOnset,
    requestMic,
    teardown,
  } = useOnsetDetection(micSettings.sensitivity);

  useEffect(() => {
    preloadPercusionSounds(Object.values(ECO_SOUND_PATHS));
  }, []);

  useEffect(() => {
    setPresentation(pagePhase !== 'lobby');
    return () => setPresentation(false);
  }, [pagePhase, setPresentation]);

  const clearFlash = useCallback(() => {
    if (flashTimerRef.current != null) {
      window.clearTimeout(flashTimerRef.current);
    }
    flashTimerRef.current = window.setTimeout(() => setTapFlash(null), 220);
  }, []);

  const resetEngine = useCallback((nextExercise: EcoRitmoExercise) => {
    const next = new RhythmTapEngine(nextExercise);
    setEngine(next);
    engineRef.current = next;
    playedAttacksRef.current = new Set();
    setActiveCellIndex(-1);
    setCountdown(COUNTDOWN_BEATS);
    setScore(0);
    setPassed(false);
    setFaultCount(0);
    setTapFlash(null);
  }, []);

  const startRound = useCallback(
    (nextExercise: EcoRitmoExercise) => {
      setMicSettings(loadEcoMicSettings());
      soundPathRef.current = ECO_SOUND_PATHS[nextExercise.soundId];
      setExercise(nextExercise);
      resetEngine(nextExercise);
      phaseStartRef.current = performance.now();
      playedAttacksRef.current = new Set();
      setPagePhase('demo');
    },
    [resetEngine]
  );

  const finishToResults = useCallback(
    (finalScore: number) => {
      const eng = engineRef.current;
      setScore(finalScore);
      setPassed(eng.passThreshold());
      setFaultCount(eng.getFaultCount());
      setPagePhase('results');
      setActiveCellIndex(-1);
      stopDetection();
      setOnOnset(null);
    },
    [stopDetection, setOnOnset]
  );

  const applyTap = useCallback(
    (now = performance.now()) => {
      const eng = engineRef.current;
      if (pagePhase !== 'play' || !eng.isUserActive()) return;

      const result = eng.handleTap(now);
      setFaultCount(eng.getFaultCount());

      if (result.verdict === 'hit' || result.verdict === 'late') {
        playPercusionSound(soundPathRef.current);
        setTapFlash('hit');
      } else if (result.verdict === 'rest_fault' || result.verdict === 'early_fault') {
        setTapFlash('fault');
      }
      clearFlash();

      if (result.finished) {
        finishToResults(result.score);
      }
    },
    [pagePhase, clearFlash, finishToResults]
  );

  const handleTap = useCallback(() => {
    applyTap();
  }, [applyTap]);

  useEffect(() => {
    if (!micUsesMic || pagePhase !== 'play') {
      stopDetection();
      setOnOnset(null);
      return;
    }

    let cancelled = false;
    setOnOnset(() => applyTap());

    void (async () => {
      await requestMic();
      if (!cancelled) startDetection();
    })();

    return () => {
      cancelled = true;
      setOnOnset(null);
      stopDetection();
    };
  }, [micUsesMic, pagePhase, applyTap, startDetection, stopDetection, setOnOnset, requestMic]);

  useEffect(() => {
    if (pagePhase !== 'play' || micSettings.inputMode === 'tap') return;

    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      e.preventDefault();
      applyTap();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pagePhase, micSettings.inputMode, applyTap]);

  useEffect(() => teardown, [teardown]);

  useEffect(() => {
    if (pagePhase === 'lobby' || pagePhase === 'results') return;

    const loop = () => {
      const eng = engineRef.current;
      const now = performance.now();
      const elapsed = now - phaseStartRef.current;

      if (pagePhase === 'demo') {
        const tMs = elapsed;
        setActiveCellIndex(eng.cellIndexAt(tMs));

        for (const atk of eng.timeline.attacks) {
          if (!attackShouldSound(atk.abcTok)) continue;
          if (playedAttacksRef.current.has(atk.index)) continue;
          if (tMs >= atk.startMs) {
            playedAttacksRef.current.add(atk.index);
            playPercusionSound(soundPathRef.current);
          }
        }

        if (tMs >= eng.timeline.totalMs + DEMO_TAIL_MS) {
          phaseStartRef.current = now;
          setCountdown(COUNTDOWN_BEATS);
          setPagePhase('countdown');
          setActiveCellIndex(-1);
        }
      } else if (pagePhase === 'countdown') {
        const beatMs = (60 / eng.timeline.bpm) * 1000;
        const beat = Math.floor(elapsed / beatMs);
        const remaining = COUNTDOWN_BEATS - beat;
        setCountdown(Math.max(0, remaining));

        if (remaining <= 0 && elapsed >= COUNTDOWN_BEATS * beatMs + 200) {
          eng.resetUserAttempts();
          eng.beginUserPhase(now);
          phaseStartRef.current = now;
          setPagePhase('play');
          setCountdown(0);
        }
      } else if (pagePhase === 'play') {
        const tMs = eng.getElapsedMs(now);
        setActiveCellIndex(eng.cellIndexAt(tMs));

        const tickResult = eng.tick(now);
        if (tickResult?.finished) {
          finishToResults(tickResult.score);
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [pagePhase, finishToResults]);

  const handleRetry = () => {
    resetEngine(exercise);
    phaseStartRef.current = performance.now();
    setPagePhase('demo');
  };

  const handleBackToLobby = () => {
    stopDetection();
    setOnOnset(null);
    setPagePhase('lobby');
    setActiveCellIndex(-1);
  };

  return (
    <JuniorLayout immersive presentation={isPresentation}>
      <EcoLayoutTuningProvider>
        {pagePhase === 'lobby' ? (
          <EcoRitmoBuilder onStart={startRound} />
        ) : (
          <ActivityShell
            title="Eco rítmico"
            subtitle={exercise.title}
            backTo="/ritmo"
            backLabel="Ritmo"
            immersive
            lockViewport
            presentation={isPresentation}
          >
            <EcoRitmoStage
              exercise={exercise}
              phase={pagePhase}
              engine={engine}
              activeCellIndex={activeCellIndex}
              countdown={countdown}
              score={score}
              passed={passed}
              faultCount={faultCount}
              tapFlash={tapFlash}
              micSettings={micSettings}
              micActive={micActive}
              micVolume={micVolume}
              micError={micError}
              presentation={isPresentation}
              onTap={handleTap}
              onRetry={handleRetry}
              onBackToLobby={handleBackToLobby}
            />
          </ActivityShell>
        )}
      </EcoLayoutTuningProvider>
    </JuniorLayout>
  );
}

export default function EcoRitmicoPage() {
  return (
    <RequireJuniorSession>
      <PresentationModeProvider>
        <EcoRitmicoPageInner />
      </PresentationModeProvider>
    </RequireJuniorSession>
  );
}
